<template>
  <v-card class="feedback-settings-card" rounded="lg" elevation="1">
    <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div class="d-flex align-center">
        <v-icon color="primary" class="mr-2">mdi-thumb-down-outline</v-icon>
        <span>{{ $t('taxFeedbackReasonSettingsTitle') }}</span>
      </div>
      <div class="d-flex align-center ga-2">
        <v-chip size="small" color="primary" variant="tonal">
          {{ items.length }} {{ $t('taxFeedbackReasonSettingsCount') }}
        </v-chip>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-content-save-outline"
          :loading="saving"
          :disabled="loading"
          data-testid="tax-feedback-save"
          @click="save"
        >
          {{ $t('taxFeedbackReasonSettingsSave') }}
        </v-btn>
      </div>
    </v-card-title>

    <v-card-text>
      <v-alert type="info" variant="tonal" class="mb-4" density="comfortable">
        {{ $t('taxFeedbackReasonSettingsHelp') }}
      </v-alert>

      <v-alert v-if="errorMessage" type="error" variant="tonal" closable class="mb-4" @click:close="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
      <v-alert v-if="successMessage" type="success" variant="tonal" closable class="mb-4" @click:close="successMessage = ''">
        {{ successMessage }}
      </v-alert>

      <div class="table-scroll">
        <v-data-table
          :headers="headers"
          :items="items"
          item-value="_key"
          :loading="loading"
          class="elevation-1 my-data-table"
          density="comfortable"
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          data-testid="tax-feedback-reason-table"
        >
          <template #item.sortOrder="{ index }">
            <div class="order-actions">
              <v-btn icon="mdi-chevron-up" size="small" variant="text" :disabled="index === 0" :aria-label="$t('taxFeedbackReasonMoveUp')" @click="move(index, -1)" />
              <span>{{ index + 1 }}</span>
              <v-btn icon="mdi-chevron-down" size="small" variant="text" :disabled="index === items.length - 1" :aria-label="$t('taxFeedbackReasonMoveDown')" @click="move(index, 1)" />
            </div>
          </template>

          <template #item.code="{ item }">
            <v-text-field
              v-model="item.code"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="item.id > 0"
              :error="Boolean(codeErrors[item.id || item._key])"
              :error-messages="codeErrors[item.id || item._key]"
              data-testid="tax-feedback-reason-code"
            />
          </template>

          <template #item.labelEn="{ item }">
            <v-text-field v-model="item.labelEn" variant="outlined" density="compact" hide-details :placeholder="$t('taxFeedbackReasonLabelEn')" />
          </template>
          <template #item.labelTh="{ item }">
            <v-text-field v-model="item.labelTh" variant="outlined" density="compact" hide-details :placeholder="$t('taxFeedbackReasonLabelTh')" />
          </template>
          <template #item.labelZh="{ item }">
            <v-text-field v-model="item.labelZh" variant="outlined" density="compact" hide-details :placeholder="$t('taxFeedbackReasonLabelZh')" />
          </template>
          <template #item.enabled="{ item }">
            <v-switch v-model="item.enabled" color="success" density="compact" hide-details inset :aria-label="$t('taxFeedbackReasonEnabled')" />
          </template>
          <template #item.actions="{ index }">
            <v-btn icon="mdi-delete-outline" color="error" variant="text" :aria-label="$t('taxFeedbackReasonDelete')" @click="remove(index)" />
          </template>

          <template #loading>
            <div class="py-8 text-center"><v-progress-circular indeterminate color="primary" /></div>
          </template>
          <template #no-data>
            <div class="py-8 text-center text-medium-emphasis">{{ $t('taxFeedbackReasonSettingsEmpty') }}</div>
          </template>
        </v-data-table>
      </div>

      <v-btn class="mt-4" variant="outlined" color="primary" prepend-icon="mdi-plus" data-testid="tax-feedback-add" @click="add">
        {{ $t('taxFeedbackReasonSettingsAdd') }}
      </v-btn>
    </v-card-text>
  </v-card>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import taxFeedbackSettingsApi from '../../services/taxFeedbackSettingsApi';

const items = ref([]);
const loading = ref(false);
const saving = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const codeErrors = ref({});
let nextKey = 1;

const headers = computed(() => [
  { title: '#', key: 'sortOrder', width: '84px', sortable: false },
  { title: 'Code', key: 'code', minWidth: '180px', sortable: false },
  { title: 'English', key: 'labelEn', minWidth: '220px', sortable: false },
  { title: 'Thai', key: 'labelTh', minWidth: '220px', sortable: false },
  { title: 'Chinese', key: 'labelZh', minWidth: '220px', sortable: false },
  { title: 'Enabled', key: 'enabled', width: '100px', sortable: false },
  { title: '', key: 'actions', width: '64px', sortable: false },
]);

function toItem(item) {
  return {
    id: item.id || 0,
    code: item.code || '',
    labelEn: item.labelEn || '',
    labelTh: item.labelTh || '',
    labelZh: item.labelZh || '',
    enabled: item.enabled !== false,
    sortOrder: item.sortOrder || 0,
    _key: nextKey++,
  };
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    items.value = (await taxFeedbackSettingsApi.getSettings()).map(toItem);
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    loading.value = false;
  }
}

function add() {
  items.value.push(toItem({ sortOrder: items.value.length + 1 }));
}

function remove(index) {
  items.value.splice(index, 1);
  renumber();
}

function move(index, direction) {
  const target = index + direction;
  if (target < 0 || target >= items.value.length) return;
  const [item] = items.value.splice(index, 1);
  items.value.splice(target, 0, item);
  renumber();
}

function renumber() {
  items.value.forEach((item, index) => { item.sortOrder = index + 1; });
}

function validate() {
  const errors = {};
  const seen = new Map();
  items.value.forEach((item) => {
    const code = item.code.trim().toLowerCase();
    const key = item.id || item._key;
    if (!code) errors[key] = 'Code is required.';
    else if (seen.has(code)) {
      errors[key] = 'Code must be unique.';
      errors[seen.get(code)] = 'Code must be unique.';
    } else seen.set(code, key);
  });
  codeErrors.value = errors;
  return Object.keys(errors).length === 0;
}

async function save() {
  successMessage.value = '';
  if (!validate()) return;
  saving.value = true;
  errorMessage.value = '';
  try {
    const saved = await taxFeedbackSettingsApi.saveSettings(items.value.map(({ _key, ...item }) => ({
      ...item,
      code: item.code.trim().toLowerCase(),
    })));
    items.value = saved.map(toItem);
    successMessage.value = 'Tax feedback reasons saved.';
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.feedback-settings-card { overflow: hidden; }
.table-scroll { overflow-x: auto; }
.order-actions { display: flex; align-items: center; justify-content: center; gap: 2px; }
.order-actions .v-btn { min-width: 28px; width: 28px; height: 28px; }
</style>
